import { useCallback, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import clsx from "clsx";
import * as yup from "yup";
import { useStore } from "../../../stores";
import { FallbackImage, Field } from "../../../components";
import locale from "../../../constants/locale";
import PrivateNotes from "./PrivateNotes";
import styles from "./ChatInfo.module.scss";
import Labels from "./Labels";
import SendToBoard from "../../contacts/components/SendToBoard";
import { ChatChannel } from "../../../models";
import ArchiveButton from "./ArchiveButton";
import { collect } from "collect.js";
import CreateTask from "./messageComposer/CreateTask";
import ChatOrigin from "./ChatOrigin";

const editableFields = ["first_name", "last_name"];
const sortOrder = ["fb_id", "username", "first_name", "last_name"];

// WhatsApp usernames (2026): a phone-less contact's fb_id holds the BSUID
// ("US.1349…") instead of a phone number.
const isBsuid = (value?: string) =>
  !!value && /^[A-Z]{2}\.[A-Za-z0-9.]+$/.test(value);

const ChatInfo = observer(
  ({ defaultTab, onClose }: { defaultTab?: string; onClose: () => void }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const { users } = useStore("users");
    const { selectedBot } = useStore("bots");

    const {
      selectedChat,
      saveData,
      setAsContact,
      setBlockStatus,
      refreshProfile,
      setChat360View,
      findConversationStatus,
    } = useStore("chats");

    const handleContactToggle = useCallback(() => {
      setLoading(true);
      setAsContact(!selectedChat?.isContact).finally(() => setLoading(false));
    }, [selectedChat?.isContact, setAsContact]);

    const handleBlockToggle = useCallback(() => {
      setLoading(true);
      setBlockStatus(!selectedChat?.isBlocked).finally(() => setLoading(false));
    }, [selectedChat?.isBlocked, setBlockStatus]);

    const handleProfileUpdate = useCallback(() => {
      setLoading(true);
      refreshProfile().finally(() => setLoading(false));
    }, [refreshProfile]);

    const tabs = {
      details: t(locale.details),
      labels: t(locale.labels),
      notes: t(locale.notes),
      pipeline: t(locale.pipeline),
    } as any;

    const [tab, setTab] = useState(defaultTab ?? "details");

    const contactsTableHeader = useMemo(
      () =>
        selectedBot?.customization?.contactsTable.header
          ?.filter(
            (f) =>
              ![
                "firstName",
                "lastName",
                "channel",
                "createdAt",
                "lastMessage",
              ].includes(f)
          )
          ?.map((f) => `e_cus_${f}`) || [],
      [selectedBot?.customization?.contactsTable.header]
    );

    if (!selectedChat) {
      return null;
    }

    const schema = yup
      .object()
      .shape(
        editableFields.reduce((p, n) => ({ ...p, [n]: yup.string() }), {})
      );

    return (
      <section
        className={clsx("full-screen-mobile is-active", styles.container)}
      >
        <div
          className="p-5"
          style={{
            height: "100%",
            overflowX: "hidden",
            overflowY: "auto",
          }}
        >
          <div
            className={clsx(
              "is-flex has-border-bottom is-align-items-center px-4 mb-3 is-hidden-desktop",
              styles.header
            )}
          >
            <div className="is-flex is-flex-grow-1 is-justify-content-flex-end">
              <span className="icon is-small is-clickable" onClick={onClose}>
                <i className="fas fa-times"></i>
              </span>
            </div>
          </div>
          <div className="is-flex is-justify-content-center">
            <figure
              className="image is-96x96 is-align-self-center is-clickable"
              onClick={() => setChat360View(true)}
            >
              <FallbackImage
                className="is-rounded"
                src={selectedChat.image}
                fallback="/assets/user.png"
                alt={selectedChat.firstName}
              />
            </figure>
          </div>
          <h1 className="title is-4 mt-3 is-align-self-center has-text has-text-centered">
            {selectedChat.firstName} {selectedChat.lastName}
          </h1>

          <div
            style={{ gap: "1rem" }}
            className="is-flex is-justify-content-space-between is-flex-wrap-wrap"
          >
            <button
              className={clsx(styles.actionBtn, "button")}
              type="button"
              disabled={loading}
              onClick={handleContactToggle}
            >
              <span className="icon is-small">
                <img
                  src={`/assets/user-${
                    selectedChat.isContact ? "remove" : "add"
                  }.svg`}
                  alt=""
                />
              </span>
              <span>
                {t(
                  selectedChat.isContact
                    ? locale.removeFromContacts
                    : locale.addAsContact
                )}
              </span>
            </button>
            <button
              className={clsx(styles.actionBtn, "button")}
              type="button"
              disabled={loading}
              onClick={handleBlockToggle}
            >
              <span className="icon is-small">
                <i className="fas fa-ban"></i>
              </span>
              <span>
                {t(selectedChat.isBlocked ? locale.unblockUser : locale.spam)}
              </span>
            </button>
            <ArchiveButton />
            <CreateTask className={clsx(styles.actionBtn, "button")} />
            {selectedChat.channel === ChatChannel.FacebookMessenger && (
              <button
                className={clsx(styles.actionBtn, "button")}
                type="button"
                disabled={loading}
                onClick={handleProfileUpdate}
              >
                <span className="icon is-small">
                  <i className="fab fa-facebook"></i>
                </span>
                <span>{t(locale.updateProfileInfo)}</span>
              </button>
            )}
          </div>

          <div className="is-flex is-justify-content-space-between pt-4">
            {Object.keys(tabs).map((key) => (
              <div
                key={key}
                onClick={() => setTab(key as any)}
                className={clsx(styles.selectTab, {
                  [styles.selectTabActive]: key === tab,
                })}
              >
                {tabs[key as any]}
              </div>
            ))}
          </div>

          <div className="mt-4">
            {tab === "details" && selectedBot && <ChatOrigin bot={selectedBot} chatId={selectedChat.id} />}
            {tab === "details" && (
              <Formik
                key={`chat-info-${selectedChat?.id}-${selectedChat?.conversationStatusId}`}
                initialValues={{
                  ...selectedChat.data,
                  "Conversation Status": findConversationStatus(
                    selectedChat?.conversationStatusId
                  )?.name,
                  assignee: selectedChat.data?.assignee
                    ? users?.find((f) => f.id === selectedChat.data?.assignee)
                        ?.name
                    : undefined,
                }}
                validationSchema={schema}
                onSubmit={async (data: any) => {
                  const cleanData: any = {};
                  editableFields
                    .concat(contactsTableHeader)
                    .forEach((f) => (cleanData[f] = data[f]));

                  await saveData(cleanData);
                }}
              >
                {({ isSubmitting }) => (
                  <Form>
                    <div>
                      {!!selectedChat.data &&
                        collect(selectedChat.data)
                          .keys()
                          .merge(contactsTableHeader)
                          .merge(["Conversation Status"])
                          .unique()
                          .values()
                          .filter((f: any) => f !== "e_cus_mentioned_users")
                          .sort((a: any, b: any) => {
                            // Get positions in sortOrder array
                            const aIndex = sortOrder.indexOf(a);
                            const bIndex = sortOrder.indexOf(b);

                            // If both items are in sortOrder, compare their positions
                            if (aIndex !== -1 && bIndex !== -1) {
                              return aIndex - bIndex;
                            }
                            // If only a is in sortOrder, it comes first
                            if (aIndex !== -1) {
                              return -1;
                            }
                            // If only b is in sortOrder, it comes first
                            if (bIndex !== -1) {
                              return 1;
                            }
                            // If neither is in sortOrder, sort alphabetically
                            return a.localeCompare(b);
                          })
                          .map((key: any) => (
                            <Field
                              key={key}
                              name={key}
                              label={
                                key.startsWith("e_cus_")
                                  ? key.substring("e_cus_".length)
                                  : key === "username"
                                  ? "Username"
                                  : key === "fb_id" &&
                                    isBsuid(selectedChat.data?.fb_id)
                                  ? "WhatsApp ID"
                                  : t(key)
                              }
                              disabled={
                                isSubmitting ||
                                (!editableFields.includes(key) &&
                                  !contactsTableHeader.includes(key))
                              }
                            />
                          ))}
                      <div className="has-text-centered">
                        <button
                          className={clsx(
                            "button is-primary has-text-weight-medium has-rounded-corners",
                            {
                              "is-loading": isSubmitting,
                            }
                          )}
                          type="submit"
                        >
                          {t(locale.save)}
                        </button>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            )}
            {tab === "labels" && <Labels />}
            {tab === "notes" && (
              <PrivateNotes
                open={tab === "notes"}
                onClose={() => setTab("details")}
              />
            )}
            {tab === "pipeline" && (
              <SendToBoard
                className="my-0 is-relative"
                contactId={`${selectedChat.id}`}
                contact={selectedChat}
                displayCardButton
              />
            )}
          </div>
        </div>
      </section>
    );
  }
);

export default ChatInfo;
